import { useState } from "react";
import { AlertCircle, ChevronDown } from "lucide-react";
import { DatePickerField } from "@/components/ui/date-picker-field";
import { cn } from "@/lib/utils";
import { pickupLabel, dropLabel, type WizardFields } from "./wizardTypes";

/**
 * Guided wizard — STEP 3: DATES.
 *
 * Two main dates drive everything: pickup (defaults to today) and delivery
 * (required on bid loads, optional on quotes — the form's rule). Changing a
 * main date writes it into every stop of that type; for multi-stop loads a
 * collapsible section allows per-stop overrides. Dates land on each stop's
 * scheduled_date, and the save path's NOT-NULL fallback ladder covers gaps.
 */

interface WizardDatesStepProps {
  fields: WizardFields;
  postType: "load" | "quote";
  onChange: (patch: Partial<WizardFields>) => void;
  error?: string | null;
}

export default function WizardDatesStep({ fields, postType, onChange, error }: WizardDatesStepProps) {
  const [perStopOpen, setPerStopOpen] = useState(false);
  const deliveryRequired = postType === "load";

  const setPickupDate = (v: string | null) => {
    const date = v || "";
    onChange({
      pickupDate: date,
      stops: fields.stops.map((s) => (s.stop_type === "pickup" ? { ...s, scheduled_date: date } : s)),
    });
  };

  const setDeliveryDate = (v: string | null) => {
    const date = v || "";
    onChange({
      deliveryDate: date,
      stops: fields.stops.map((s) => (s.stop_type === "delivery" ? { ...s, scheduled_date: date } : s)),
    });
  };

  const setStopDate = (id: string, v: string | null) => {
    onChange({ stops: fields.stops.map((s) => (s.id === id ? { ...s, scheduled_date: v || "" } : s)) });
  };

  const isMultiStop = fields.stops.length > 2;
  let pickupIdx = 0;
  let dropIdx = 0;

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2.5">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-destructive" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-medium text-muted-foreground mb-1 block">
            Pickup date <span className="text-destructive">*</span>
          </label>
          <DatePickerField
            value={fields.pickupDate || null}
            onChange={setPickupDate}
            placeholder="Pick pickup date"
            displayFormat="MMM d, yyyy"
            className="h-10 text-sm"
          />
        </div>
        <div>
          <label className="text-[11px] font-medium text-muted-foreground mb-1 block">
            Delivery date{" "}
            {deliveryRequired ? (
              <span className="text-destructive">*</span>
            ) : (
              <span className="text-muted-foreground/70">(optional for quotes)</span>
            )}
          </label>
          <DatePickerField
            value={fields.deliveryDate || null}
            onChange={setDeliveryDate}
            placeholder="Pick delivery date"
            displayFormat="MMM d, yyyy"
            className="h-10 text-sm"
          />
        </div>
      </div>

      {!deliveryRequired && (
        <p className="text-[11px] text-muted-foreground">
          Quotes don't need a delivery date — add one if you have it.
        </p>
      )}

      {isMultiStop && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <button
            type="button"
            onClick={() => setPerStopOpen((o) => !o)}
            className="w-full flex items-center justify-between px-4 py-2.5 text-left"
          >
            <span className="text-xs font-semibold text-foreground">
              Different dates per stop? <span className="font-normal text-muted-foreground">(optional)</span>
            </span>
            <ChevronDown
              className={cn("h-4 w-4 text-muted-foreground transition-transform", perStopOpen && "rotate-180")}
            />
          </button>
          {perStopOpen && (
            <div className="px-4 pb-3 pt-1 space-y-2.5 border-t border-border">
              <p className="text-[10px] text-muted-foreground">
                Changing a main date above re-fills every stop of that type.
              </p>
              {fields.stops.map((stop) => {
                const isPickup = stop.stop_type === "pickup";
                const label = isPickup
                  ? pickupLabel(pickupIdx++, fields.pickupCount)
                  : dropLabel(dropIdx++, fields.dropCount);
                return (
                  <div key={stop.id} className="flex items-center gap-3">
                    <span className="w-20 flex-shrink-0 text-[11px] font-medium text-muted-foreground truncate">
                      {label}
                      {stop.city ? ` — ${stop.city}` : ""}
                    </span>
                    <div className="flex-1">
                      <DatePickerField
                        value={stop.scheduled_date || null}
                        onChange={(v) => setStopDate(stop.id, v)}
                        placeholder={isPickup ? "Pickup date" : "Delivery date"}
                        displayFormat="MMM d, yyyy"
                        className="h-9 text-xs"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
